import { Controller, useForm } from "react-hook-form";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useContext, useEffect, useRef, useState } from "react";
import axios from "../../api/axios";
import AuthContext from "../../context/AuthProvider";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import logo from "../../utils/images/ridePlus.png";
import ProfilePicture from "../profile/profile-picture";
import { queryClient } from "../../App";
import { phoneRegex } from "../../utils/validationPattern";
import useOtpResendCooldown from "../../hooks/useOtpResendCooldown";
import OtpResendButton from "../../components/otpResendButton";

function RegisterWithWeb({ setOpenRegister }) {
  // USESTATE & USEREF
  const [openDrawer, setOpenDrawer] = useState(false);
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [inputType, setInputType] = useState("password");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmInputType, setConfirmInputType] = useState("password");
  const [file, setFile] = useState("");
  const [open, setOpen] = useState(false);
  const [requestOtp, setRequestOtp] = useState(false);

  const [tosAndPPUrl, setTosAndPPUrl] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const password = useRef({});
  const userData = useRef({});

  // Cooldown hook for OTP resend functionality
  const cooldownHook = useOtpResendCooldown(
    "register",
    userData.current?.phone
  );

  // SNACKBAR
  const { enqueueSnackbar } = useSnackbar();

  const location = useLocation();
  const { redirectUri } = useContext(AuthContext);
  const searchParams = new URLSearchParams(location.search);
  const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
  const clientIdFromUrl =
    searchParams.get("client_id") || searchParams.get("clientId");
  const tenantIdFromUrl =
    searchParams.get("tenant_id") || searchParams.get("tenantId");
  const isConsentFlow = !!clientIdFromUrl && !!tenantIdFromUrl;
  const clientIdForOtp =
    clientIdFromUrl ||
    redirectSearchParams.get("client_id") ||
    redirectSearchParams.get("clientId");

  // REACT HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onChange",
  });

  // password.current = watch("password", "");

  // MUTATION & USEQUERY
  const { mutate, isLoading, data, error } = useMutation((data) => {
    return axios.post("/register", data, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
  });
  const {
    isFetching: otpIsFetching,
    data: otpData,
    error: otpError,
  } = useQuery(
    ["otp_for_web", userData.current?.phone, clientIdForOtp],
    () =>
      axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "signup",
          phone: userData.current.phone,
          ...(clientIdForOtp && { client_id: clientIdForOtp }),
        },
      }),
    {
      refetchOnWindowFocus: false,
      enabled: requestOtp && userData.current?.phone !== undefined,
      retry: false,
    }
  );

  const {
    isFetching: tosLinksIsFetching,
    isLoading: tosLinksIsLoading,
  } = useQuery(
    ["tos-pp-url", clientIdFromUrl, tenantIdFromUrl, isConsentFlow],
    () =>
      axios.get("/clients/tos-pp-url", {
        params: {
          client_id: clientIdFromUrl,
          tenant_id: tenantIdFromUrl,
        },
      }),
    {
      enabled: isConsentFlow,
      retry: false,
      onSuccess: (response) => {
        const data = response?.data?.data || response?.data;
        const privacyPolicyUrl = data?.privacy_policy_url || "";
        const termsOfServiceUrl = data?.terms_of_service_url || "";
        if (privacyPolicyUrl || termsOfServiceUrl) {
          setTosAndPPUrl({
            privacy_policy_url: privacyPolicyUrl,
            terms_of_service_url: termsOfServiceUrl,
          });
          setAgreeToTerms(false);
          return;
        }
        setTosAndPPUrl(null);
        setAgreeToTerms(false);
      },
      onError: () => {
        setTosAndPPUrl(null);
        setAgreeToTerms(false);
      },
    }
  );

  // USEEFFECT
  useEffect(() => {
    if (data) {
      enqueueSnackbar(`You're succesfully registered!`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      queryClient.clear();
      setOpenRegister(false);
    }
  }, [data, enqueueSnackbar, setOpenRegister]);

  useEffect(() => {
    if (otpData && userData.current.phone !== undefined) {
      enqueueSnackbar(`Code sent to your phone!`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      cooldownHook.startCooldown(); // Start cooldown after successful OTP request
      setRequestOtp(false);
      queryClient.clear();
    }
  }, [enqueueSnackbar, otpData, cooldownHook]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(
        error?.response?.data?.error?.message ||
        error?.message ||
        "Network Error!",
        {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: 2000,
        }
      );
    }
    if (otpError && userData.current.phone !== undefined) {
      enqueueSnackbar(
        otpError?.response?.data?.error?.message ||
        otpError?.message ||
        "Network Error!",
        {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: 2000,
        }
      );
    }
  }, [enqueueSnackbar, error, otpError]);

  // EVENT HANDLERS & HELPERS
  const handleClick = () => {
    setOpenRegister(false);
  };
  const handleRegister = (form) => {
    if (isConsentFlow && (tosLinksIsFetching || tosLinksIsLoading)) {
      enqueueSnackbar(
        "Please wait while loading Privacy Policy and Terms.",
        {
          variant: "info",
          preventDuplicate: true,
          autoHideDuration: 2500,
        }
      );
      return;
    }

    if (tosAndPPUrl && !agreeToTerms) {
      enqueueSnackbar(
        "Please agree to the Privacy Policy and Terms to continue.",
        {
          variant: "warning",
          preventDuplicate: true,
          autoHideDuration: 2500,
        }
      );
      return;
    }

    setOpenDrawer(true);
    setPhone(form?.phone);
    userData.current = form;
    setRequestOtp(true);
  };
  const handleSignup = (form) => {
    userData.current = { ...userData.current, otp: form.otp };
    const dataToBeSent =
      file !== ""
        ? { ...userData.current, profile_picture: file }
        : userData.current;
    mutate(dataToBeSent);
  };

  //RENDER
  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 2,
          }}
        >
          <Avatar
            sx={{ width: "80px", height: "65px", borderRadius: 0 }}
            src={logo}
          />
          <Typography sx={{ fontSize: 20, fontWeight: "bold", color: "text.primary" }}>
            Create your account
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit(handleRegister)}
            sx={{ mt: 1 }}
          >
            <Divider />
            <Box
              sx={{
                maxHeight: "70vh",
                overflowY: "visible",
                overflowX: "hidden",
                pr: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  px: { xs: 0, md: 2 },
                }}
              >
                <ProfilePicture
                  edit={true}
                  isOpen={open}
                  setIsOpen={setOpen}
                  imageSrc={file}
                  setFile={setFile}
                />
              </Box>
              <Controller
                name="first_name"
                control={control}
                rules={{
                  required: "First Name is required",
                }}
                render={({ field }) => (
                  <TextField
                    sx={{
                      mt: 1.5,
                      mb: 1,
                    }}
                    label="First Name"
                    fullWidth
                    autoComplete="first_name"
                    defaultValue={userData.current?.first_name}
                    error={!!errors?.first_name}
                    helperText={
                      errors?.first_name ? errors.first_name.message : null
                    }
                    size="small"
                    {...field}
                  />
                )}
              />
              <Controller
                name="middle_name"
                control={control}
                rules={{
                  required: "Middle Name is required",
                }}
                render={({ field }) => (
                  <TextField
                    sx={{
                      mt: 0.5,
                      mb: 1,
                    }}
                    variant="outlined"
                    label="Middle Name"
                    fullWidth
                    autoComplete="middle_name"
                    defaultValue={userData.current?.middle_name}
                    error={!!errors?.middle_name}
                    helperText={
                      errors?.middle_name ? errors.middle_name.message : null
                    }
                    size="small"
                    {...field}
                  />
                )}
              />
              <Controller
                name="last_name"
                control={control}
                rules={{
                  required: "Last Name is required",
                }}
                render={({ field }) => (
                  <TextField
                    sx={{
                      mt: 0.5,
                      mb: 1,
                    }}
                    variant="outlined"
                    label="Last Name"
                    fullWidth
                    autoComplete="last_name"
                    defaultValue={userData.current?.last_name}
                    error={!!errors?.last_name}
                    helperText={
                      errors?.last_name ? errors.last_name.message : null
                    }
                    size="small"
                    {...field}
                  />
                )}
              />
              <Controller
                name="gender"
                control={control}
                rules={{
                  required: "Gender is required",
                }}
                render={({ field }) => (
                  <TextField
                    sx={{
                      mt: 0.5,
                      mb: 1,
                    }}
                    variant="outlined"
                    label="Gender"
                    fullWidth
                    select
                    size="small"
                    autoComplete="gender"
                    defaultValue={userData.current?.gender}
                    error={!!errors?.gender}
                    helperText={errors?.gender ? errors.gender.message : null}
                    {...field}
                  >
                    <MenuItem value="Male">{"Male"}</MenuItem>
                    <MenuItem value="Female">{"Female"}</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone is required",
                  pattern: {
                    value: phoneRegex,
                    message: "Invalid input",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    sx={{
                      mt: 0.5,
                      mb: 1,
                    }}
                    variant="outlined"
                    label="Phone"
                    fullWidth
                    autoComplete="phone"
                    defaultValue={userData.current?.phone}
                    error={!!errors?.phone}
                    helperText={errors?.phone ? errors.phone.message : null}
                    size="small"
                    {...field}
                  />
                )}
              />
              {/* <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    sx={{
                      mt: 0.5,
                      mb: 1,
                    }}
                    variant="outlined"
                    label="Email"
                    fullWidth
                    autoComplete="email"
                    defaultValue={userData.current?.email}
                    error={!!errors?.email}
                    helperText={errors?.email ? errors.email.message : null}
                    size="small"
                    {...field}
                  />
                )}
              /> */}
              {/* <Controller
                name="password"
                control={control}
                rules={{
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "At least 6 characters is required.",
                  },
                  maxLength: {
                    value: 32,
                    message: "The maximum characters allowed is 32.",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    id="password-input"
                    sx={{
                      mt: 0.5,
                      mb: 1,
                    }}
                    label="Password"
                    fullWidth
                    type={inputType}
                    autoComplete="current-password"
                    defaultValue={userData.current?.password}
                    error={!!errors?.password}
                    helperText={
                      errors?.password ? errors.password.message : null
                    }
                    size="small"
                    {...field}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShowPassword(
                                showPassword === false ? true : false
                              );
                              setInputType(
                                showPassword === false ? "text" : "password"
                              );
                            }}
                          >
                            {showPassword ? (
                              <Visibility sx={{ fontSize: "inherit" }} />
                            ) : (
                              <VisibilityOff sx={{ fontSize: "inherit" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              /> */}
              {/* <Controller
                name="confirm_password"
                control={control}
                rules={{
                  validate: (value) =>
                    value === password.current || "The passwords do not match",
                }}
                render={({ field }) => (
                  <TextField
                    id="confirm-password-input"
                    sx={{
                      mt: 0.5,
                      mb: 1.5,
                    }}
                    label="Confirm Password"
                    fullWidth
                    type={confirmInputType}
                    defaultValue={userData.current?.confirm_password}
                    autoComplete="confirm-current-password"
                    error={!!errors?.confirm_password}
                    helperText={
                      errors?.confirm_password
                        ? errors.confirm_password.message
                        : null
                    }
                    size="small"
                    {...field}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShowConfirmPassword(
                                showConfirmPassword === false ? true : false
                              );
                              setConfirmInputType(
                                showConfirmPassword === false
                                  ? "text"
                                  : "password"
                              );
                            }}
                          >
                            {showConfirmPassword ? (
                              <Visibility sx={{ fontSize: "inherit" }} />
                            ) : (
                              <VisibilityOff sx={{ fontSize: "inherit" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              /> */}

              {tosAndPPUrl && (
                <Box sx={{ mb: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreeToTerms}
                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                        sx={{ p: 0.5, mt: 0.25 }}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        I agree to the{" "}
                        {tosAndPPUrl?.privacy_policy_url && (
                          <Link
                            href={tosAndPPUrl.privacy_policy_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Privacy Policy
                          </Link>
                        )}
                        {tosAndPPUrl?.privacy_policy_url &&
                        tosAndPPUrl?.terms_of_service_url
                          ? " and "
                          : ""}
                        {tosAndPPUrl?.terms_of_service_url && (
                          <Link
                            href={tosAndPPUrl.terms_of_service_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms of Service
                          </Link>
                        )}
                        .
                      </Typography>
                    }
                    sx={{
                      alignItems: "center",
                      m: 0,
                      gap: 1,
                    }}
                  />
                </Box>
              )}
            </Box>
            <Divider />
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                size="small"
                sx={{
                  bgcolor: "action.hover",
                  ":hover": { bgcolor: "action.selected" },
                  color: "text.primary",
                  borderRadius: 2,
                  textTransform: "none"
                }}
                onClick={handleClick}
              >
                Back
              </Button>
              <Button
                size="small"
                sx={{
                  bgcolor: "secondary.main",
                  ":hover": { bgcolor: "secondary.dark" },
                  color: "secondary.contrastText",
                  borderRadius: 2,
                  textTransform: "none"
                }}
                type="submit"
                disabled={otpIsFetching || (isConsentFlow && tosLinksIsFetching)}
              >
                Next
                {otpIsFetching && userData.current?.phone !== undefined && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "secondary.contrastText",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Button>
              {openDrawer && !otpIsFetching && !otpError && (
                <Dialog
                  open={openDrawer}
                  fullScreen
                  PaperProps={{
                    sx: {
                      bgcolor: 'background.default'
                    }
                  }}
                >
                  <Grid
                    container
                    sx={{
                      minHeight: "100vh",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Container component="main" maxWidth="xs">
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          boxShadow: theme => theme.palette.mode === 'light'
                            ? `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`
                            : `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
                          p: 4,
                          borderRadius: 4,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Avatar
                          sx={{ width: "100px", height: "80px", m: 1 }}
                          src={logo}
                        />
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: 20,
                            textAlign: "center",
                          }}
                        >
                          Enter a code
                        </Typography>
                        <Box
                          component="form"
                          onSubmit={handleSubmit(handleSignup)}
                          sx={{ mt: 1 }}
                        >
                          <Divider />
                          <Typography
                            sx={{ fontWeight: "medium", fontSize: 16, pt: 1 }}
                          >
                            Please fill the code sent to your phone number:{" "}
                            {phone} via SMS.
                          </Typography>
                          <Controller
                            name="otp"
                            control={control}
                            rules={{
                              required: "Code is required",
                            }}
                            render={({ field }) => (
                              <TextField
                                sx={{
                                  mt: 1.5,
                                  mb: 1,
                                }}
                                fullWidth
                                size="small"
                                label="Code"
                                autoComplete="otp"
                                autoFocus
                                helperText={
                                  errors?.otp ? errors?.otp?.message : ""
                                }
                                error={errors?.otp}
                                {...field}
                              />
                            )}
                          />
                          <Box sx={{ mt: 1 }}>
                            <OtpResendButton
                              cooldownHook={cooldownHook}
                              onResend={() => setRequestOtp(true)}
                              isLoading={otpIsFetching}
                              size="small"
                              showMessage={true}
                            />
                          </Box>
                          <Divider />
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              sx={{
                                ":hover": { bgcolor: "#FFAA00" },
                                backgroundColor: "action.hover",
                                color: "text.primary",
                                border: '1px solid',
                                borderColor: 'divider',
                                textTransform: "none",
                                ":hover": { bgcolor: "action.selected" },
                              }}
                              onClick={() => setOpenDrawer(false)}
                            >
                              Back
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              type="submit"
                              sx={{
                                ":hover": { bgcolor: "secondary.dark" },
                                backgroundColor: "secondary.main",
                                color: "secondary.contrastText",
                                textTransform: "none",
                              }}
                              disabled={isLoading}
                            >
                              Submit
                              {isLoading && (
                                <CircularProgress
                                  size={24}
                                  sx={{
                                    color: "secondary.contrastText",
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    marginTop: "-12px",
                                    marginLeft: "-12px",
                                  }}
                                />
                              )}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Container>
                  </Grid>
                </Dialog>
              )}
            </Box>
          </Box>
        </Box>
      </Container>
    </Grid >
  );
}
export default RegisterWithWeb;
